import {useAppDataStore} from "@/mobx/appDataStore.jsx";
import {useEffect, useState} from "react";
import {observer} from "mobx-react";

export default observer(function AdminAppConfig() {
  const appDataStore = useAppDataStore();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pendingValues, setPendingValues] = useState({});
  const [saving, setSaving] = useState({});

  const userScopes = appDataStore.user?.scopes || [];
  const hasMasterScope = Array.isArray(userScopes) && userScopes.includes('master');

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      const res = await appDataStore.get('/config/list');
      const list = res.configs || [];
      setConfigs(list);
      const initial = {};
      list.forEach(c => { initial[c.key] = c.value; });
      setPendingValues(initial);
    } catch (err) {
      alert('Error al cargar configuraciones: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setPendingValues(prev => ({...prev, [key]: value}));
  };

  const handleSave = async (config) => {
    try {
      setSaving(prev => ({...prev, [config.key]: true}));
      await appDataStore.post('/config/update', {key: config.key, value: pendingValues[config.key]});
      setConfigs(prev => prev.map(c => c.key === config.key ? {...c, value: pendingValues[config.key]} : c));
    } catch (err) {
      alert('Error al guardar: ' + err.message);
    } finally {
      setSaving(prev => ({...prev, [config.key]: false}));
    }
  };

  const isDirty = (config) => pendingValues[config.key] !== config.value;

  const renderInput = (config) => {
    const value = pendingValues[config.key] ?? config.value;

    if (config.type === 'boolean') {
      return (
        <button
          onClick={() => handleChange(config.key, value === 'true' ? 'false' : 'true')}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${value === 'true' ? 'bg-green-500' : 'bg-gray-300'}`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${value === 'true' ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      );
    }

    if (config.type === 'select') {
      const options = config.options || [];
      return (
        <select
          value={value}
          onChange={e => handleChange(config.key, e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded text-sm min-w-[280px]"
        >
          {options.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      );
    }

    if (config.type === 'number') {
      return (
        <input
          type="number"
          value={value}
          onChange={e => handleChange(config.key, e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded text-sm w-40"
        />
      );
    }

    return (
      <input
        type="text"
        value={value}
        onChange={e => handleChange(config.key, e.target.value)}
        className="px-3 py-2 border border-gray-300 rounded text-sm min-w-[280px]"
      />
    );
  };

  if (!hasMasterScope) {
    return (
      <div className="p-2 max-w-7xl mx-auto text-black">
        <h2 className="text-2xl font-bold mb-4 text-gray-800">Configuración del Sistema</h2>
        <div className="bg-red-50 border border-red-300 rounded-lg p-6 max-w-2xl">
          <h3 className="text-lg font-bold text-red-800 mb-2">Acceso Denegado</h3>
          <p className="text-red-700">Se requiere el scope <strong>Master</strong>.</p>
        </div>
      </div>
    );
  }

  // Agrupar por categoría
  const grouped = configs.reduce((acc, c) => {
    const cat = c.category || 'general';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(c);
    return acc;
  }, {});

  return (
    <div className="p-2 max-w-4xl mx-auto text-black">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">Configuración del Sistema</h2>

      {loading ? (
        <div className="text-center py-8 text-gray-500">Cargando configuraciones...</div>
      ) : (
        <div className="space-y-8">
          {Object.entries(grouped).map(([category, items]) => (
            <div key={category} className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-100 px-4 py-3 border-b border-gray-200">
                <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">{category}</h3>
              </div>
              <div className="divide-y divide-gray-100">
                {items.map(config => (
                  <div key={config.key} className="px-4 py-4 flex items-start justify-between gap-6">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800">{config.label}</p>
                      {config.description && (
                        <p className="text-xs text-gray-500 mt-1">{config.description}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1 font-mono">{config.key}</p>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {renderInput(config)}
                      <button
                        onClick={() => handleSave(config)}
                        disabled={!isDirty(config) || saving[config.key]}
                        className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
                          isDirty(config)
                            ? 'bg-green-600 hover:bg-green-700 text-white'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        {saving[config.key] ? 'Guardando...' : 'Guardar'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});